import { useState, useEffect, useRef } from "react";

// Detects which way the user is scrolling
const useScrollDirection = (threshold = 10, scrollContainerRef = null) => {
  const [scrollDirection, setScrollDirection] = useState("up");
  const lastScrollYRef = useRef(0);
  const lastDirectionRef = useRef("up");
  const lastUpdateTimeRef = useRef(0);

  useEffect(() => {
    let ticking = false;

    const updateScrollDirection = (scrollY) => {
      const scrollDiff = scrollY - lastScrollYRef.current;
      const now = Date.now();
      const timeDiff = now - lastUpdateTimeRef.current;
      
      // If scroll jumped a lot in a short time (like anchor link), ignore it
      // This prevents the header from hiding when clicking TOC links
      // Also ignore scroll updates for a brief period after large jumps
      if (Math.abs(scrollDiff) > 500 && timeDiff < 200) {
        lastScrollYRef.current = scrollY > 0 ? scrollY : 0;
        lastUpdateTimeRef.current = now;
        ticking = false;
        return;
      }
      
      // Ignore very small scroll changes
      if (Math.abs(scrollDiff) < threshold) {
        ticking = false;
        return;
      }

      const newDirection = scrollDiff > 0 ? "down" : "up";
      
      // Only update if direction actually changed (add hysteresis)
      if (newDirection !== lastDirectionRef.current) {
        setScrollDirection(newDirection);
        lastDirectionRef.current = newDirection;
      }
      
      lastScrollYRef.current = scrollY > 0 ? scrollY : 0;
      lastUpdateTimeRef.current = now;
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        const scrollY = scrollContainerRef?.current 
          ? scrollContainerRef.current.scrollTop
          : window.pageYOffset || document.documentElement.scrollTop;
        
        window.requestAnimationFrame(() => updateScrollDirection(scrollY));
        ticking = true;
      }
    };

    const scrollElement = scrollContainerRef?.current || window;
    
    scrollElement.addEventListener("scroll", onScroll, { passive: true });

    return () => scrollElement.removeEventListener("scroll", onScroll);
  }, [threshold, scrollContainerRef]);

  return scrollDirection;
};

export default useScrollDirection;
